/* eslint-disable sort-keys */
import type { Theme } from '@ksyos/design-system';
import type { JSX } from 'react';
import { Box, Divider, SectionHeader } from '@ksyos/design-system';
import type {
    IStepCount,
    IStepGroup,
    OutclinicDashboardServiceCode,
} from '@common/interfaces/api/outclinicDashboardApi';
import { OutclinicDashboardStepCardFilterItem } from '@frontend/components/pages/outclinicDashboard/outclinicDashboardStepCardFilters/OutclinicDashboardStepCardFilterItem';

type outClinicDashboardStepCardFilterTopProps = {
    activeDashboardStepIdentifier: string | null;
    counts: IStepCount[];
    groups: IStepGroup[];
    onChange: (dashboardStepIdentifier: string) => void;
    serviceCode: OutclinicDashboardServiceCode;
};

export function OutclinicDashboardStepCardFiltersTop(
    props: Readonly<outClinicDashboardStepCardFilterTopProps>
): JSX.Element {
    return (
        <>
            <SectionHeader title="Overzicht proces en filter" />
            <Box
                overflowY="auto"
                p={4}
            >
                <Box
                    display="grid"
                    gap={10}
                    gridTemplateAreas={`
                    "item1 item2 item3 item4 item4"
                    ". . . item6 item5"
                    `}
                    gridTemplateColumns="repeat(5, max-content)"
                    p={2}
                    sx={{
                        '.outclinic-dashboard-step-card-filter-item1': {
                            '&:before': {
                                border: '1px solid',
                                borderColor: (theme: Theme) => theme.colors.neutral500,
                                content: '""',
                                display: 'block',
                                height: '1px',
                                position: 'absolute',
                                top: '54%',
                                width: 'calc(100% + 40px)',
                            },
                            gridArea: 'item1',
                        },
                        '.outclinic-dashboard-step-card-filter-item2': {
                            '&:before': {
                                border: '1px solid',
                                borderColor: (theme: Theme) => theme.colors.neutral500,
                                content: '""',
                                display: 'block',
                                height: '1px',
                                position: 'absolute',
                                top: '54%',
                                width: 'calc(100% + 40px)',
                            },
                            gridArea: 'item2',
                        },
                        '.outclinic-dashboard-step-card-filter-item3': {
                            '&:before': {
                                border: '1px solid',
                                borderColor: (theme: Theme) => theme.colors.neutral500,
                                content: '""',
                                display: 'block',
                                height: '1px',
                                position: 'absolute',
                                right: '50%',
                                top: '54%',
                                width: 'calc(50% + 20px)',
                            },
                            '&:after': {
                                border: '1px dashed',
                                borderColor: (theme: Theme) => theme.colors.neutral500,
                                content: '""',
                                display: 'block',
                                height: '1px',
                                left: '50%',
                                position: 'absolute',
                                top: '54%',
                                width: 'calc(50% + 20px)',
                                zIndex: '-1',
                            },
                            gridArea: 'item3',
                        },
                        '.outclinic-dashboard-step-card-filter-item4': {
                            '&:before': {
                                border: '1px dashed',
                                borderColor: (theme: Theme) => theme.colors.neutral500,
                                content: '""',
                                display: 'block',
                                height: '1px',
                                position: 'absolute',
                                top: '54%',
                                width: 'calc(100% + 40px)',
                            },
                            '.outclinic-dashboard-step-card-filter-item-steps': {
                                '&:before, &:after': {
                                    borderRight: '2px solid',
                                    borderColor: (theme: Theme) => theme.colors.neutral500,
                                    content: '""',
                                    display: 'block',
                                    height: '12px',
                                    position: 'absolute',
                                    right: '-16px',
                                    top: '117%',
                                    transform: 'rotate(135deg)',
                                    width: '0',
                                },
                                '&:after': {
                                    right: '-24px',
                                    transform: 'rotate(45deg)',
                                },
                                flexDirection: 'row',
                            },
                            gridArea: 'item4',
                        },
                        '.outclinic-dashboard-step-card-filter-item5': {
                            '&:before': {
                                border: '1px dashed',
                                borderColor: (theme: Theme) => theme.colors.neutral500,
                                content: '""',
                                display: 'block',
                                height: '1px',
                                position: 'absolute',
                                top: '54%',
                                width: 'calc(100% + 40px)',
                            },
                            '&:after': {
                                border: '1px dashed',
                                borderColor: (theme: Theme) => theme.colors.neutral500,
                                bottom: '45%',
                                content: '""',
                                display: 'block',
                                height: '118%',
                                position: 'absolute',
                                right: '-20px',
                                zIndex: '-1',
                            },
                            gridArea: 'item5',
                        },
                        '.outclinic-dashboard-step-card-filter-item6': {
                            '&:before': {
                                border: '1px dashed',
                                borderColor: (theme: Theme) => theme.colors.neutral500,
                                content: '""',
                                display: 'block',
                                height: '1px',
                                position: 'absolute',
                                top: '54%',
                                width: 'calc(100% + 40px)',
                            },
                            '&:after': {
                                border: '1px dashed',
                                borderColor: (theme: Theme) => theme.colors.neutral500,
                                bottom: '45%',
                                content: '""',
                                display: 'block',
                                height: '118%',
                                left: '-20px',
                                position: 'absolute',
                            },
                            '.outclinic-dashboard-step-card-filter-item-steps': {
                                '&:before, &:after': {
                                    borderRight: '2px solid',
                                    borderColor: (theme: Theme) => theme.colors.neutral500,
                                    content: '""',
                                    display: 'block',
                                    height: '12px',
                                    left: '-52px',
                                    position: 'absolute',
                                    top: '-39%',
                                    transform: 'rotate(45deg)',
                                    width: '0',
                                },
                                '&:after': {
                                    left: '-44px',
                                    transform: 'rotate(135deg)',
                                },
                                flexDirection: 'row-reverse',
                            },
                            gridArea: 'item6',
                        },
                    }}
                >
                    {props.groups.map((group, index) => (
                        <OutclinicDashboardStepCardFilterItem
                            activeDashboardStepIdentifier={props.activeDashboardStepIdentifier}
                            className={`outclinic-dashboard-step-card-filter-item${index + 1}`}
                            counts={props.counts}
                            key={group.title}
                            onChange={props.onChange}
                            steps={group.steps}
                            title={group.title}
                        />
                    ))}
                </Box>
            </Box>
            <Divider />
        </>
    );
}
